import { ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { generateSchedule, applySchedule } from '@/lib/api'

/**
 * Composable for managing the auto-schedule feature
 * Handles the modal flow: form -> loading -> preview, with regeneration and apply
 */
export function useAutoSchedule() {
  const toast = useToast()

  // Modal flow state
  const modalVisible = ref(false)
  const step = ref('form') // 'form' | 'loading' | 'preview'

  // Form inputs
  const targetWeek = ref(null) // Date object or ISO string
  const guidance = ref('')
  const adjustment = ref('')

  // Generated schedule state
  const schedule = ref(null)
  const aiNotes = ref('')
  const unscheduledStaff = ref([])
  const validationWarnings = ref([])
  const existingShiftsCount = ref(0)
  const cached = ref(false)
  const generationTimeMs = ref(0)

  // Loading/error state
  const isGenerating = ref(false)
  const isApplying = ref(false)
  const error = ref(null)

  // Regeneration tracking
  const regenerateCount = ref(0)

  /**
   * Open the modal and reset state
   */
  const openModal = () => {
    // Reset all state to defaults
    step.value = 'form'
    targetWeek.value = null
    guidance.value = ''
    adjustment.value = ''
    schedule.value = null
    aiNotes.value = ''
    unscheduledStaff.value = []
    validationWarnings.value = []
    existingShiftsCount.value = 0
    cached.value = false
    generationTimeMs.value = 0
    error.value = null
    isGenerating.value = false
    isApplying.value = false
    regenerateCount.value = 0
    modalVisible.value = true
  }

  /**
   * Close the modal
   */
  const closeModal = () => {
    modalVisible.value = false
  }

  /**
   * Generate a schedule
   * @param {number} storeId - The store ID
   * @param {boolean} forceRegenerate - Bypass cache and add entropy
   * @returns {Promise<void>}
   */
  const generate = async (storeId, forceRegenerate = false) => {
    isGenerating.value = true
    step.value = 'loading'
    error.value = null

    try {
      const requestData = {
        store_id: storeId,
        target_week_start: targetWeek.value,
        guidance: guidance.value || null,
        adjustment: adjustment.value || null,
        force_regenerate: forceRegenerate,
        regenerate_count: regenerateCount.value
      }

      const response = await generateSchedule(requestData)
      const data = response.data

      if (!data.success) {
        throw new Error(data.message || 'Failed to generate schedule')
      }

      // Populate schedule state
      schedule.value = data.schedule
      aiNotes.value = data.ai_notes || ''
      unscheduledStaff.value = data.unscheduled_staff || []
      validationWarnings.value = data.validation_warnings || []
      existingShiftsCount.value = data.existing_shifts_count || 0
      cached.value = data.cached || false
      generationTimeMs.value = data.generation_time_ms || 0

      // Move to preview step
      step.value = 'preview'
    } catch (err) {
      error.value = err.message || 'Failed to generate schedule'
      toast.add({
        severity: 'error',
        summary: 'Generation Failed',
        detail: error.value,
        life: 5000
      })
      // Go back to form on error
      step.value = 'form'
    } finally {
      isGenerating.value = false
    }
  }

  /**
   * Regenerate the schedule with adjustments
   * @param {number} storeId - The store ID
   * @returns {Promise<void>}
   */
  const regenerate = async (storeId) => {
    regenerateCount.value++
    await generate(storeId, true)
  }

  /**
   * Apply the generated schedule to the database
   * @param {number} storeId - The store ID
   * @param {boolean} confirmOverwrite - Confirm overwriting existing shifts
   * @returns {Promise<Object>} Result object with success flag and requiresConfirmation if needed
   */
  const apply = async (storeId, confirmOverwrite = false) => {
    isApplying.value = true
    error.value = null

    try {
      const requestData = {
        store_id: storeId,
        schedule: schedule.value,
        confirm_overwrite: confirmOverwrite
      }

      const response = await applySchedule(requestData)
      const data = response.data

      // Handle requires_confirmation response
      if (data.requires_confirmation) {
        return {
          requiresConfirmation: true,
          existingShiftsCount: data.existing_shifts_count,
          message: data.message
        }
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to apply schedule')
      }

      // Success
      toast.add({
        severity: 'success',
        summary: 'Schedule Applied',
        detail: data.message || `Schedule created with ${data.shifts_created} shifts`,
        life: 3000
      })

      closeModal()
      return { success: true }
    } catch (err) {
      error.value = err.message || 'Failed to apply schedule'
      toast.add({
        severity: 'error',
        summary: 'Apply Failed',
        detail: error.value,
        life: 5000
      })
      return { success: false, error: error.value }
    } finally {
      isApplying.value = false
    }
  }

  /**
   * Go back to the form step and clear adjustment
   */
  const backToForm = () => {
    step.value = 'form'
    adjustment.value = ''
  }

  return {
    // Modal flow state
    modalVisible,
    step,

    // Form inputs
    targetWeek,
    guidance,
    adjustment,

    // Generated schedule state
    schedule,
    aiNotes,
    unscheduledStaff,
    validationWarnings,
    existingShiftsCount,
    cached,
    generationTimeMs,

    // Loading/error state
    isGenerating,
    isApplying,
    error,

    // Regeneration tracking
    regenerateCount,

    // Methods
    openModal,
    closeModal,
    generate,
    regenerate,
    apply,
    backToForm
  }
}
